import { AbstractionError, assertNoLeak, project, type PoolGenome } from '@morph/spec';
import type { LensState } from './candidate.js';
import type { GenerationRequest, RetrievedGenome } from './types.js';
import {
  fold, POOLED_KNOBS, resolve, uniqueArgmax, type PoolTastePrior,
} from './taste/fold.js';
import type { Scope } from './taste/knobs.js';
import { domainOf } from './taste/knobs.js';
import type { TastePriors } from './types.js';

/* ─────────────────────────────────────────────────────────── the write gate */

const canonicalise = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonicalise);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(Object.keys(value as object).sort()
      .map((key) => [key, canonicalise((value as Record<string, unknown>)[key])]));
  }
  return value;
};

/** A genome's identity is its CONTENT, so the same layout contributed twice is
 *  one genome. Decision 0040: *"a genome id is a deterministic hash of the
 *  projection, not an object, vault, user, or revision id."* */
export const genomeId = (genome: PoolGenome): string => {
  const canonical = JSON.stringify(canonicalise(genome));
  let hash = 2166136261;
  for (let i = 0; i < canonical.length; i += 1) {
    hash ^= canonical.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `genome_${(hash >>> 0).toString(36)}`;
};

/**
 * The write boundary: an accepted lens becomes an anonymised genome, or nothing.
 *
 * `privateValues` comes from the accepting host, which is the only boundary that
 * still knows content and identity. Searching the finished projection is
 * deliberately redundant with `project()`'s structural stripping — decision
 * 0039: *"'We did not copy a field' is not the privacy boundary."* A field added
 * to `LayoutSpec` later must not silently widen what a genome publishes.
 *
 * `undefined` for a lens that is offered but not accepted. 0039 makes the unit
 * of contribution *"one accepted spec"*: pooling a lens the person never chose
 * publishes a layout on their behalf.
 */
export function genomeFromAcceptedLens(
  lens: LensState, privateValues: readonly string[],
): PoolGenome | undefined {
  if (!lens.accepted) return undefined;
  const genome = project(lens.accepted.spec);
  assertNoLeak(genome, [...privateValues]);
  return genome;
}

/**
 * Testable guard on the exact value that crosses generation's pool boundary.
 *
 * A `RetrievedGenome` is a genome plus two fields that are NOT part of one —
 * `genomeId`, the content hash it is filed under, and `score`, this request's
 * fit. Task `0124` made `assertNoLeak` assert closed-vocabulary membership over
 * every string it is given, and neither of those is a vocabulary term, so
 * handing it the wrapper refuses every retrieval.
 *
 * So the membership half runs over the genome-shaped fields, which is where
 * decision `0039`'s invariant actually applies, and the needle half runs over
 * the WHOLE value — because a private string smuggled into `genomeId` is
 * exactly the kind of thing the needle search is still here to name.
 *
 * `genomeId` gets its own shape check rather than an exemption. It is minted as
 * `genome_<base36>` by `genomeId()` above; anything else in that field did not
 * come from there.
 */
const GENOME_ID = /^genome_[0-9a-z]+$/;

/**
 * How deep the whole-value needle walk goes before it refuses.
 *
 * Same argument as `assertClosedVocabulary`'s: a bound on a GUARD refuses
 * rather than stops, because what it has not looked at is precisely what it was
 * asked about.
 */
const RETRIEVED_DEPTH = 32;

/**
 * Every string anywhere in a value, with a cycle and a depth refusal.
 *
 * **Two sets, and the second one is not an optimisation.** `onPath` holds this
 * value's ancestors, so finding a value in it means the value contains itself:
 * a cycle, refused. `done` holds values already walked to completion, whose
 * strings are therefore already collected.
 *
 * Without `done`, a shared subtree is walked once per reference. A value 26
 * levels deep where each level holds two references to the same child is a few
 * hundred bytes and 2^26 walks — a refusal that costs more than the attack it
 * refuses. Found by re-reading this function an hour after writing it, against
 * `assertClosedVocabulary`, which had needed exactly the same pair for exactly
 * the same reason and got it first. The lesson did not travel one file.
 */
function stringsIn(value: unknown): { found: string[]; refused: string[] } {
  const found: string[] = [];
  const refused: string[] = [];
  const onPath = new Set<object>();
  const done = new Set<object>();
  const walk = (v: unknown, path: string, depth: number): void => {
    if (depth > RETRIEVED_DEPTH) {
      refused.push(`${path}: nested deeper than ${RETRIEVED_DEPTH}, so nothing below `
        + 'it could be checked');
      return;
    }
    if (typeof v === 'string') { found.push(v); return; }
    if (!v || typeof v !== 'object') return;
    if (onPath.has(v)) { refused.push(`${path}: the retrieved genome contains a cycle`); return; }
    if (done.has(v)) return;
    onPath.add(v);
    if (Array.isArray(v)) v.forEach((item, i) => walk(item, `${path}/${i}`, depth + 1));
    else {
      for (const [k, item] of Object.entries(v)) {
        /**
         * The KEY counts too.
         *
         * @Codex, P2 on `78f39a7`: *"`stringsIn()` still visits only property
         * values, not property names. An externally supplied row with an extra
         * own key such as `{ ...valid, ['Plein Soleil']: 1 }` therefore passes
         * the needle scan, and the later reconstructed genome drops that extra
         * key before `assertNoLeak()`, certifying a retrieved value that
         * contains the supplied private string."*
         *
         * The same shape as the finding that made this a whole-wrapper scan in
         * the first place, one level in: the fix widened WHAT is walked and not
         * WHERE inside each object it looks. A JavaScript object carries text
         * in two places and this read one of them.
         *
         * And the reconstruction below is what makes it invisible — the four
         * fields are rebuilt, so the extra key never reaches `assertNoLeak` to
         * be caught there instead.
         */
        found.push(k);
        walk(item, `${path}/${k}`, depth + 1);
      }
    }
    onPath.delete(v);
    done.add(v);
  };
  walk(value, '', 0);
  return { found, refused };
}

export function assertRetrievedGenomeHasNoLeak(
  retrieved: RetrievedGenome, privateValues: readonly string[],
): void {
  /**
   * The needles over the WHOLE value, not over the four fields we then check.
   *
   * @Codex, P2 on #184: *"When an externally supplied retrieved row carries an
   * extra own field such as `provenance: 'Plein Soleil'`, this destructuring
   * drops it before `assertNoLeak()` and the function separately examines only
   * `genomeId`, so the row is certified even when that extra field matches a
   * supplied private value. This contradicts the function's whole-value
   * retrieval-boundary contract."*
   *
   * Right, and the destructuring is what made it invisible: a line naming four
   * fields reads like a projection and behaves like a filter. `project()` has
   * the same shape and closed the same hole by rebuilding `target` field by
   * field — the difference being that a projection produces a NEW value, so
   * what it drops is genuinely gone, while a guard that drops a field has
   * merely agreed not to look at it.
   *
   * Of the two remedies they offered, this is the second. Refusing unknown keys
   * outright would be stronger and is wrong here: callers legitimately pass a
   * whole `PoolGenome` spread beside `genomeId` and `score` — `vocabularyVersion`,
   * `target`, `shape` and `capabilities` all ride along — so a key allowlist
   * would refuse the retrievals this function exists to certify. Membership
   * stays scoped to the four fields that are a genome's own; the needles are
   * not scoped at all, because a private value does not care which field it is
   * sitting in.
   */
  const { found: strings, refused } = stringsIn(retrieved);
  if (refused.length) throw new AbstractionError(refused);
  /**
   * A WHOLE token, never a fragment — @Codex, P2 on `18eae3f`, and it is
   * this repo's oldest guard defect arriving at its newest guard: a private
   * value like "a" or "id" matched INSIDE the fixed key "palette" or
   * "genomeId", because `stringsIn` deliberately collects keys (the
   * `78f39a7` finding) and `includes` decided on three characters of a
   * longer opaque token. The boundary is applied only on the side where the
   * needle's own edge is a word character, so `$5` and `(2001)` stay
   * findable — the same correction `shape.ts` carries, stated the same way:
   * the needle names what to look for; it does not get to decide on a
   * fragment of something else.
   */
  const WORD = /[0-9A-Za-z]/;
  const escapeNeedle = (v: string): string => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // NOT `\b`: underscore is a word character to `\b`, and this repo's ids
  // use it as the SEPARATOR — `genome_secret` must still surrender "secret"
  // (the earlier finding this guard already pins), while "a" inside
  // "palette" must not match. So the boundary is alphanumeric-only, applied
  // through lookarounds on the sides where the needle's own edge is
  // alphanumeric.
  const whole = (v: string): RegExp => new RegExp(
    (WORD.test(v[0]!) ? '(?<![0-9A-Za-z])' : '') + escapeNeedle(v)
    + (WORD.test(v[v.length - 1]!) ? '(?![0-9A-Za-z])' : ''));
  const anywhere = privateValues.filter(
    (needle) => needle.length > 0 && strings.some((text) => whole(needle).test(text)));
  if (anywhere.length) {
    throw new AbstractionError(
      anywhere.map((n) => `private value "${n}" found in the retrieved genome`));
  }
  const { structure, density, typography, palette } = retrieved;
  assertNoLeak({ structure, density, typography, palette } as unknown as PoolGenome,
    [...privateValues]);
  if (typeof retrieved.genomeId !== 'string' || !GENOME_ID.test(retrieved.genomeId)) {
    throw new AbstractionError(
      [`genomeId "${String(retrieved.genomeId)}" was not minted by genomeId()`]);
  }
  /**
   * The `genomeId` needle check that used to live here is GONE, and its rule is
   * not.
   *
   * It was added for @Codex's earlier finding — *"a private value can survive
   * in an ID such as `genome_secret`: the lowercase-alphanumeric pattern
   * accepts it, and these lines explicitly skip the needle check"* — and the
   * whole-value scan at the top of this function now covers `genomeId` along
   * with every other string, which is the same rule stated once instead of per
   * field. Two needle checks, one of them scoped to a single field, is how the
   * gap they found in the next round came to exist.
   *
   * The SHAPE check above stays. An id `genomeId()` could not have minted is
   * worth refusing on its own, and that is a different question from what the
   * id contains.
   */
}

/* ──────────────────────────────────────────────────────────────── the store */

/**
 * The pool, for one browser and one person.
 *
 * Decision 0039 limits phase 1 to a private, same-browser pool: the shape
 * fingerprint is retained for matching and is *"not anonymous"*, so a shared
 * pool waits on cohort protection that does not exist yet. Nothing here is a
 * transport, and that is deliberate — a pool with one contributor exercises
 * every invariant `0040` names.
 */
export class PrivateGenomeStore {
  readonly #byId = new Map<string, PoolGenome>();

  /**
   * How a genome is named. `genomeId` unless a caller supplies better.
   *
   * Injected because the collision refusal below names a wider digest as the
   * fix, and a fix that cannot be supplied is a sentence rather than a remedy.
   * It is also what makes that refusal reachable from a test: producing a real
   * 32-bit FNV-1a collision is a search, and what is under test is the
   * response, not the arithmetic.
   */
  readonly #idOf: (genome: PoolGenome) => string;

  constructor(idOf: (genome: PoolGenome) => string = genomeId) { this.#idOf = idOf; }

  /** Returns the id, whether or not it was already held. Deduplicated by
   *  content, so ten contributions of one layout are one genome — the store
   *  half of `0040`'s *"ten copies of one genome cannot become ten times as
   *  persuasive."* */
  contribute(genome: PoolGenome): string {
    const id = this.#idOf(genome);
    /**
     * A COLLISION is not a duplicate.
     *
     * @Codex, P2 on #184: *"When two distinct accepted projections produce the
     * same 32-bit FNV-1a value, `PrivateGenomeStore.contribute()` treats them as
     * one genome and silently retains only the first, making retrieval and
     * aggregation depend on insertion order rather than projected content. A
     * 32-bit namespace reaches a roughly even chance of some collision around
     * 77,000 distinct genomes."*
     *
     * Right, and the deduplication above it is deliberate — `0040`'s *"ten
     * copies of one genome cannot become ten times as persuasive"* — so the id
     * has two jobs and only the first is safe at 32 bits. Comparing the
     * canonical content on an equal id costs one `JSON.stringify` on the rare
     * path and gives the exact answer, where a wider digest would only make the
     * wrong answer rarer. Decision 0040 says the id is *"a deterministic hash of
     * the projection"*, and this keeps that true while refusing to let a hash
     * stand in for the projection.
     */
    const already = this.#byId.get(id);
    if (already && JSON.stringify(canonicalise(already)) !== JSON.stringify(canonicalise(genome))) {
      throw new GenomeIdCollision(id);
    }
    // Cloned in, and cloned out again below. @Codex's delivery on #184 used
    // `structuredClone` at both ends and it is the stronger version: without
    // it the store holds a reference the contributor can still mutate, so a
    // genome could change after being pooled and a snapshot taken "before"
    // would silently show the later value.
    if (!this.#byId.has(id)) this.#byId.set(id, structuredClone(genome));
    return id;
  }

  get size(): number { return this.#byId.size; }

  /**
   * A DETACHED snapshot, taken once.
   *
   * The array is a copy, so a contribution arriving after this call cannot
   * reach a generation already using it. That is `0039`'s churn rule made
   * structural rather than promised: *"later contributions cannot mutate the
   * snapshot, the `PoolTastePrior`, the candidate being previewed, or an
   * accepted lens."* A getter returning the live map would leave the rule
   * depending on nobody holding one too long.
   */
  snapshot(): readonly { id: string; genome: PoolGenome }[] {
    return [...this.#byId].map(([id, genome]) => ({ id, genome: structuredClone(genome) }));
  }
}

/* ──────────────────────────────────────────────────────────── the retriever */

/**
 * How well a genome fits this request, in `[0,1]`. Structural only — a genome
 * carries no content to match on, by construction.
 *
 * Three closed-vocabulary facts, evenly weighted. @Codex's delivery on #184
 * scored this way and it is better than the version here first: mine matched on
 * role kinds and media alone, so **every genome scored identically for
 * `browsing` and `reading`** and "request-relative retrieval" was a claim
 * nothing could distinguish. `target.context` and `target.cardinality` are the
 * two facts that make it relative to a request rather than to a shape.
 *
 * ## There was a fourth, and it scored backwards
 *
 * `Number(genome.shape.writable === Boolean(req.shape.declaresRelations))`.
 * @Codex, P2 on #184: *"`genome.shape.writable` means that the projected layout
 * contains any read-write binding, while `req.shape.declaresRelations` only
 * means that the schema declares a relation. They are not equivalent, and the
 * real pool gesture obtains its shape directly from `deriveShape()`, which
 * never sets `declaresRelations`, so normal writable genomes lose a quarter of
 * their retrieval score while read-only genomes receive that match."*
 *
 * Worse than noise, then: the term was **inverted** on every real request, and
 * it was inverted quietly because a retrieval score has no correct answer to
 * disagree with. Removed rather than repaired, which is one of the two things
 * @Codex offered — the other needs a request-side layout-writability fact, and
 * `GenerationRequest` has none. Inventing one that a single caller supplies is
 * how `accepted` became decorative (task `0121`); a fourth axis is worth having
 * when a host actually carries the fact, and not before.
 */
function scoreAgainst(genome: PoolGenome, req: GenerationRequest): number {
  const wanted = req.shape.roles.map((r) => r.kind as string);
  const have = genome.shape.roleKinds as readonly string[];
  const union = new Set([...wanted, ...have]);
  const kinds = union.size === 0 ? 1
    : new Set(wanted.filter((k) => have.includes(k))).size / union.size;
  return (
    Number(genome.target.cardinality === req.shape.cardinality)
    + Number(genome.target.context === req.context)
    + kinds
  ) / 3;
}

/**
 * Score a SUPPLIED snapshot against this request.
 *
 * Takes the snapshot rather than a store, so this cannot consult live state:
 * the one-read rule is then a property of the signature, not of the caller's
 * discipline.
 */
export function retrieveGenomes(
  snapshot: readonly { id: string; genome: PoolGenome }[], req: GenerationRequest,
): RetrievedGenome[] {
  return snapshot.map(({ id, genome }) => ({
    genomeId: id,
    score: scoreAgainst(genome, req),
    structure: structuredClone(genome.structure),
    density: genome.density,
    typography: genome.typography,
    palette: genome.palette,
  }));
}

/* ─────────────────────────────────────────────────────────── the aggregator */

/** The only knobs a genome may vote on. Decision 0040: explicit,
 *  closed-vocabulary fields with a live compilation target. NOT
 *  `collection.flow`, `media.fit` or indexed `role.presence` — *"deriving any
 *  of those from `structure` would introduce a second, implicit projection
 *  policy."* */
export { POOLED_KNOBS } from './taste/fold.js';

/**
 * Two different projections hashed to one id.
 *
 * Loud rather than silent, because the alternatives are worse: keeping the
 * first makes retrieval depend on insertion order, and keeping the second
 * discards a layout somebody accepted. A refusal names a real event and is
 * reachable — `genomeId` is deterministic, so a person who hits this hits it
 * every time, and the fix is a wider digest rather than a retry.
 */
export class GenomeIdCollision extends Error {
  constructor(readonly id: string) {
    super(
      `two different layouts hash to ${id}. The pool cannot tell them apart, `
      + 'and a wider genome digest is the fix rather than dropping one of them',
    );
    this.name = 'GenomeIdCollision';
  }
}

export class InvalidRetrievalScore extends Error {
  constructor(genomeId: string, score: unknown) {
    super(`retrieval score for ${genomeId} is ${String(score)}, not a number in [0,1]`);
    this.name = 'InvalidRetrievalScore';
  }
}

/**
 * One snapshot's genomes become one categorical base per knob.
 *
 * Deduplicated by `genomeId` first, then each genome's score is summed onto the
 * value it votes for, then the knob's **complete domain** is normalised to 1.
 * The normalisation is what discards candidate count, which is the churn rule:
 * *"more independent genomes can change the shape of the distribution, but not
 * the amount of authority strangers receive."* `PRIOR_MASS` alone decides how
 * loudly the result speaks.
 *
 * An invalid score REFUSES the snapshot rather than being repaired. A silently
 * clamped `NaN` is a vote of unknown weight, and `0040` chose refusal on
 * purpose.
 */
/**
 * How many DISTINCT genomes an aggregate summed.
 *
 * @Codex, P2 on #184: *"`votes` is supplied as raw `retrieved.length`, although
 * `aggregate()` first discards duplicate genome IDs. With many duplicates and
 * two legitimate but closely scored unique genomes, the prior remains unchanged
 * while the inflated tolerance makes `uniqueArgmax()` turn the real winner into
 * a tie; duplicating a genome therefore changes the suggestion despite decision
 * 0040 explicitly requiring duplication to have no effect."*
 *
 * Right, and it is `0040`'s rule reached by a new route: the prior was already
 * duplicate-proof, and the TOLERANCE was not. Ten copies of one genome could
 * not make it ten times as persuasive, but they could make a real winner
 * disappear.
 *
 * Counted the same way `aggregate` counts — by genome id — so the two cannot
 * drift.
 *
 * ## And a distinct genome that scored ZERO is not a summand
 *
 * @Codex, P2 one round later: *"distinct zero-score genomes still inflate
 * `votes` even though they add nothing to any aggregate. For example, adding
 * 1,000 unique score-zero candidates to votes scored `0.5000000000001` and
 * `0.5` leaves the posterior unchanged but widens `tieTolerance()` enough to
 * turn the legitimate winner into a tie, so irrelevant retrievals alter the
 * suggestion contrary to decision 0040's sum-based distribution."*
 *
 * The same defect as the paragraph above, one substitution along: dedupe closed
 * the door on *duplicating* a genome and left it open for *adding* irrelevant
 * ones. `tieTolerance` is `summands * EPSILON * largest`, and a summand of
 * exactly `0` moves neither the total nor the error in it — floating-point
 * addition of zero is exact — so counting it buys a wider tolerance for
 * nothing and hands anyone who can put rows in the pool a way to silence it.
 *
 * ## What is still counted that arguably should not be, and why that is fine
 *
 * `aggregate` also skips a candidate whose value for a given knob is outside
 * that knob's domain, so the exactly-correct count is per-knob and this is one
 * number for all of them. It is therefore an upper bound, and the direction
 * matters: a tolerance that is too WIDE can only make `uniqueArgmax` abstain,
 * which decision `0038` already calls the safe answer. Too narrow is the one
 * that manufactures a winner. The zero-score case is different in kind rather
 * than in degree — it is unbounded, because a caller can add a thousand of
 * them — which is why it is fixed here and the other is not.
 */
export function distinctVotes(retrieved: readonly RetrievedGenome[]): number {
  /**
   * ## And the FIRST row decides, because that is the row `aggregate` keeps
   *
   * @Codex, P2, a third round on this function: *"When a supplied snapshot
   * contains duplicate rows for one `genomeId` whose first row scores zero and
   * a later duplicate scores positively, `aggregate()` keeps the first row and
   * gives that genome no weight, while this loop still counts the later
   * duplicate as a summand."*
   *
   * The two paragraphs above fixed *duplicating* a genome and *adding*
   * irrelevant ones, and both were written as "count the same way `aggregate`
   * counts". This did not: it counted a genome if ANY of its rows scored
   * positively, while `aggregate` reads only the first. One row of padding in
   * front of a positive duplicate therefore bought a summand that contributes
   * nothing — the same unbounded widening of `tieTolerance`, reached through
   * the one door the previous two left open.
   *
   * So the dedupe happens FIRST and the score test second, in that order,
   * which is the only arrangement that reproduces `aggregate`'s reading. The
   * two loops still cannot be shared — `aggregate` also validates and works
   * per-knob — so what keeps them in step is this comment and the clause that
   * plants a zero in front of a positive.
   */
  const first = new Map<string, number>();
  for (const c of retrieved) if (!first.has(c.genomeId)) first.set(c.genomeId, c.score);
  let contributing = 0;
  for (const score of first.values()) if (score > 0) contributing += 1;
  return contributing;
}

export function aggregate(retrieved: readonly RetrievedGenome[]): PoolTastePrior {
  const seen = new Map<string, RetrievedGenome>();
  for (const candidate of retrieved) {
    if (!Number.isFinite(candidate.score) || candidate.score < 0 || candidate.score > 1) {
      throw new InvalidRetrievalScore(candidate.genomeId, candidate.score);
    }
    if (!seen.has(candidate.genomeId)) seen.set(candidate.genomeId, candidate);
  }

  const out: Record<string, Record<string, number>> = {};
  for (const knob of POOLED_KNOBS) {
    const domain = domainOf(knob);
    const totals: Record<string, number> = Object.fromEntries(domain.map((v) => [v, 0]));
    let positive = 0;
    /**
     * Summed in SORTED order, so an equal multiset gives an equal total.
     *
     * @Codex, P2 on #184: *"equal 10,000-vote multisets of scores `1`, `1/3`,
     * and `1/7` summed in opposite orders can leave normalized values about
     * `8e-14` apart, well above this roughly `1.8e-15` tolerance, so
     * `uniqueArgmax()` manufactures a winner where decision 0040 requires a
     * tie."*
     *
     * The tolerance was the wrong place to answer it, and widening it back
     * would undo the previous round's fix. Floating-point addition is not
     * associative, so the error is a property of the ORDER — and the order here
     * is `Map` insertion, which is retrieval order, which is not evidence about
     * anything. Sorting the summands makes two equal multisets produce
     * bit-identical totals whatever order they arrived in, so there is no
     * error left for a tolerance to cover.
     *
     * Smallest-first, which is also the arrangement that loses the least
     * precision: adding a tiny value into a large running total is where a
     * significand gets truncated.
     */
    const per: Record<string, number[]> = {};
    const all: number[] = [];
    for (const candidate of seen.values()) {
      const value = candidate[knob] as string;
      if (!domain.includes(value)) continue;
      (per[value] ??= []).push(candidate.score);
      all.push(candidate.score);
    }
    const sum = (xs: number[]): number =>
      [...xs].sort((a, b) => a - b).reduce((a, b) => a + b, 0);
    for (const [value, scores] of Object.entries(per)) totals[value] = sum(scores);
    positive = sum(all);
    // No positive score means nothing was said. An all-zero row normalised to
    // uniform would be a claim of indifference the pool never made.
    if (positive <= 0) continue;
    /**
     * The ratio, unrounded. **The tie is protected at the comparison instead.**
     *
     * @Codex, P2 on #184: *"summing their arbitrary `[0,1]` scores can
     * introduce unequal floating-point totals — for example, `0.1 + 0.2`
     * versus `0.3`. `tasteFromPoolPrior()` later applies exact-equality
     * `uniqueArgmax`, so that rounding artefact becomes a unique winner and
     * lets the pool alter a layout where the tie contract requires
     * abstention."*
     *
     * Right, and the first fix — round every probability to nine places — was
     * wrong in a way their next review caught: *"three `0.333333333` entries
     * whose sum is `0.999999999`, so the exported `aggregate()` boundary no
     * longer returns the categorical distribution with total one required by
     * decision 0040."*
     *
     * There is no rounding that escapes this. Quantise and a three-way tie
     * loses mass; hand the remainder to one value to restore the mass and that
     * value becomes a unique winner — which is the defect the quantisation was
     * for. The two properties are only compatible if the tolerance lives where
     * the comparison is, so `uniqueArgmax` now abstains on a difference too
     * small to be evidence, and this returns the distribution `0040` asks for.
     *
     * `1/3 + 1/3 + 1/3 === 1` in IEEE-754, so equal support is exact here.
     */
    out[knob] = Object.fromEntries(domain.map((v) =>
      [v, (totals[v] ?? 0) / positive]));
  }
  return out;
}

/* ────────────────────────────────────────────── the base, seen by generation */

/**
 * The aggregate, through `fold()`, exposed to generation as taste.
 *
 * Through `fold()` rather than around it, because `0040` keeps it *"as the
 * single place where a pool base and personal evidence meet"* — so a person
 * with a ledger gets their own taste and the pool as its base, and a person
 * with none gets the pool alone. Passing `ledger` lets the same call serve both.
 *
 * Only UNIQUE winners are exposed. Decision 0038's `uniqueArgmax`: a tie is not
 * a neutral value, it is the absence of a learned override, and picking one by
 * domain order would let the order values were typed in decide an interface.
 */
export function tasteFromPoolPrior(
  prior: PoolTastePrior, now: number, ledger: readonly Parameters<typeof fold>[0][number][] = [],
  scope: Scope = { level: 'global' },
  /** How many scores were summed to make `prior`. The tie tolerance has to
   *  scale with that rather than with the domain size — see `tieTolerance`. */
  votes = 0,
): TastePriors {
  const claims = fold(ledger, { now, poolPrior: prior });
  const taste: Record<string, string> = {};
  for (const knob of POOLED_KNOBS) {
    /**
     * `resolve`, at the request's scope, not `@global` by hand.
     *
     * @Codex, P2 on #184: *"When the ledger contains applicable context or
     * surface evidence — for example, a browsing-scoped density preference —
     * `fold()` creates that scoped claim, but this lookup always reads the
     * global posterior. The pool-only global winner can therefore replace the
     * person's more specific preference when compiling a pool suggestion."*
     *
     * Which inverts the rule the pool is built on. `0040` makes `fold()` *"the
     * single place where a pool base and personal evidence meet"* and the
     * person wins where they have said something; reading `@global` reaches
     * past a preference they expressed for exactly the context they are in, to
     * a bucket the pool materialised. `resolve` already walks the chain —
     * surface, context, global — and already refuses thin surface evidence.
     * Hand-writing the key was reimplementing it wrongly.
     */
    const winner = uniqueArgmax(resolve(claims, knob, scope)?.posterior ?? {}, votes);
    if (winner !== undefined) taste[knob] = winner;
  }
  return taste as TastePriors;
}
